import React from 'react';
import { VblAnalysisResult } from '@/types/vbl';
import { VblProbabilityPlot } from './VblProbabilityPlot';
import { VblCDFPlot } from './VblCDFPlot';
import { PlotVblConfigModal } from './PlotVblConfigModal';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';
import { PlotCard } from '@/components/ui';

interface VblWeibullPanelProps {
  analysis: VblAnalysisResult | null;
  isLoading?: boolean;
  hasEnoughData?: boolean;
  sampleSize?: number;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  showCensoredData?: boolean;
  metadata?: {
    totalSamples?: number;
    failureCount?: number;
    censoredCount?: number;
    hasCensoredData?: boolean;
    degradationTimeRange?: { min: number; max: number };
    sites?: string[];
    components?: string[];
    message?: string;
  };
  censoredData?: Array<{ x: number; y: number; time: number; probability: number; caseId?: string }>;
  plotType?: 'probability' | 'cdf';
  showSettingsInSidebar?: boolean;
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

/**
 * VBL Weibull Analysis Panel - displays Weibull analysis results for VBL data
 * Exactly mirrors WeibullAnalysisPanel but for degradation time analysis
 */
const VblWeibullPanel: React.FC<VblWeibullPanelProps> = ({
  analysis,
  isLoading = false,
  hasEnoughData = true,
  sampleSize = 0,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  showCensoredData = false,
  metadata,
  censoredData,
  plotType = 'probability',
  showSettingsInSidebar = false,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}) => {
  const [showConfigModal, setShowConfigModal] = React.useState(false);
  const [localPlotType, setLocalPlotType] = React.useState(plotType);
  const [localShowConfidenceIntervals, setLocalShowConfidenceIntervals] = React.useState(showConfidenceIntervals);
  const [localConfidenceLevel, setLocalConfidenceLevel] = React.useState(confidenceLevel);

  // Update local state when props change
  React.useEffect(() => {
    setLocalPlotType(plotType);
  }, [plotType]);

  React.useEffect(() => {
    setLocalShowConfidenceIntervals(showConfidenceIntervals);
  }, [showConfidenceIntervals]);

  React.useEffect(() => {
    setLocalConfidenceLevel(confidenceLevel);
  }, [confidenceLevel]);

  // Not enough data
  if (!hasEnoughData || sampleSize < 2) {
    return (
      <PlotCard title="Insufficient Data" loading={false}>
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Insufficient Data for VBL Analysis</h3>
          <p className="text-gray-600">
            {sampleSize === 0
              ? 'No VBL records found with current filters'
              : `Only ${sampleSize} record${sampleSize === 1 ? '' : 's'} found. At least 2 records are required for Weibull analysis.`
            }
          </p>
        </div>
      </PlotCard>
    );
  }

  // No analysis results
  if (!analysis) {
    return (
      <PlotCard title="Analysis Not Available" loading={false}>
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analysis Not Available</h3>
          <p className="text-gray-600">
            Unable to perform VBL Weibull analysis with the current data.
            {metadata?.message && (
              <span className="block mt-2 text-sm">{metadata.message}</span>
            )}
          </p>
        </div>
      </PlotCard>
    );
  }

  const plotActions = showSettingsInSidebar ? (
    <div className="flex items-center gap-4 ml-auto">
      {isLoading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-onyx-bright-blue"></div>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-4">
      {isLoading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-onyx-bright-blue"></div>
      )}
      <button
        onClick={() => setShowConfigModal(true)}
        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Configure
      </button>
    </div>
  );

  return (
    <>
      <PlotCard
        title={`VBL Weibull ${localPlotType === 'probability' ? 'Probability Plot' : 'Cumulative Distribution Function'}`}
        subtitle={localPlotType === 'probability' ? 'Linearized view for assessing fit quality' : 'Probability of failure over time'}
        actions={plotActions}
      >
        {localPlotType === 'probability' ? (
          <VblProbabilityPlot
            analysis={analysis}
            showConfidenceIntervals={localShowConfidenceIntervals}
            confidenceLevel={localConfidenceLevel}
            showCensoredData={showCensoredData}
            censoredData={censoredData}
            height={512}
            colorBy={colorBy}
            markerBy={markerBy}
            sizeBy={sizeBy}
            showLegend={showLegend}
          />
        ) : (
          <VblCDFPlot
            analysis={analysis}
            showConfidenceIntervals={localShowConfidenceIntervals}
            confidenceLevel={localConfidenceLevel}
            showCensoredData={showCensoredData}
            censoredData={censoredData}
            height={512}
            colorBy={colorBy}
            markerBy={markerBy}
            sizeBy={sizeBy}
            showLegend={showLegend}
          />
        )}
      </PlotCard>

      {/* Configuration Modal */}
      {!showSettingsInSidebar && (
        <PlotVblConfigModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          plotType={localPlotType}
          onPlotTypeChange={setLocalPlotType}
          showConfidenceIntervals={localShowConfidenceIntervals}
          onToggleConfidenceIntervals={setLocalShowConfidenceIntervals}
          confidenceLevel={localConfidenceLevel}
          onConfidenceLevelChange={setLocalConfidenceLevel}
        />
      )}
    </>
  );
};

export default VblWeibullPanel;